import { computePercentage, computeValueWithoutVAT, computeValueWithVAT, round } from 'services/VATUtils';

/** ***********************************************************************
 * This file regroups all methods used to compute prices for intervention
 *   checklists. Refer to the confluence page before modifying something
 ************************************************************************ */

/* Price computation for advices */
export const computePriceWithVat = (advice, vat) => round(computeValueWithVAT(advice.price || 0, vat));

export const computePriceWithCoefAndVat = (advice, vat) =>
  round(computeValueWithVAT(advice.price * advice.coefficient || 0, vat));

export const computeDiscountWithVat = (advice, vat) => round(computeValueWithVAT(advice.discount || 0, vat));

/**
 * @param {Pick<import('modules/checklists/types/resources/advices/intervention-checklist-advice').InterventionChecklistAdvice, 'discountedPrice' | 'coefficient'>} advice
 * @param {number} vat
 * @returns {number}
 */
export const computeDiscountWithCoefAndVat = (advice, vat) =>
  round(computeValueWithVAT(advice.discountedPrice * advice.coefficient || 0, vat));

export const computeDiscountPercentage = (advice) => {
  const { discount = 0, price = 0 } = advice;
  return computePercentage(discount, price);
};

export const computeAdviceTotal = (advice, vat) => {
  const total = computeAdviceTotalWithVat(advice, vat);
  return computeValueWithoutVAT(total, vat);
};

export const computeAdviceTotalHT = ({ price = 0, discount = 0 } = {}) =>
  (Math.round(price * 100) - Math.round(discount * 100)) / 100;

export const computeAdviceTotalWithVat = (advice, vat) => {
  const priceWithVat = computePriceWithCoefAndVat(advice, vat);
  const discountWithVat = computeDiscountWithVat(advice, vat);
  return priceWithVat - discountWithVat;
};

/* Price computation for item */
// eslint-disable-next-line default-param-last
export const computeItemTotal = (item, severities = [], vat) => {
  const total = computeItemTotalWithVat(item, severities, vat);
  return computeValueWithoutVAT(total, vat);
};

// eslint-disable-next-line default-param-last
export const computeItemTotalWithVat = (item, severities = [], vat) =>
  (item.advices || [])
    .filter(a => severities.includes(a?.severity))
    .reduce((acc, advice) => acc + computeAdviceTotalWithVat(advice, vat), 0);

/* Price computation for checklist */
export const computeChecklistTotal = (checklist, severities, vat) => {
  const total = computeChecklistTotalWithVat(checklist, severities, vat);
  return computeValueWithoutVAT(total, vat);
};

export const computeChecklistTotalWithVat = (checklist, severities, vat) =>
  (checklist.items || []).reduce((acc, item) => acc + computeItemTotalWithVat(item, severities, vat), 0);

/* Price computation for checklists */
export const computeChecklistsTotalWithVat = (checklists, severities, vat) =>
  checklists.reduce((acc, checklist) => acc + computeChecklistTotalWithVat(checklist, severities, vat), 0);
